import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { Label } from './Label.js';
import { HelpText } from './HelpText.js';
import './FieldGroup.css';

export interface FieldGroupProps {
  label: ReactNode;
  /** The form control — must accept `id` and `aria-describedby` props (Input,
   * Textarea, Select, Checkbox, etc. all do). */
  children: ReactElement<{ id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }>;
  helpText?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Explicit id; auto-generated via `useId` when omitted, so callers never
   * have to hand-wire label/control/help-text association themselves. */
  id?: string;
}

/**
 * Wires Label + a form control + HelpText/error together by id, so every
 * field in the product gets the association for free instead of each screen
 * re-deriving it (and inevitably forgetting it, as the prototype did — 9
 * aria-* attributes total across 3,683 lines).
 */
export function FieldGroup({ label, children, helpText, error, required = false, id }: FieldGroupProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;
  const describedBy = [error ? errorId : null, !error && helpText ? helpId : null].filter(Boolean).join(' ') || undefined;

  const control = isValidElement(children)
    ? cloneElement(children, {
        id: fieldId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })
    : children;

  return (
    <div className="dpr-field-group">
      <Label htmlFor={fieldId} required={required}>
        {label}
      </Label>
      <div className="dpr-field-group__control">{control}</div>
      {error ? (
        <HelpText id={errorId} tone="error">
          {error}
        </HelpText>
      ) : helpText ? (
        <HelpText id={helpId}>{helpText}</HelpText>
      ) : null}
    </div>
  );
}
